import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";

import {
	InformationRight,
	InformationRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { Field } from "core/field/FieldService";
import FieldInput from "core/field/input/FieldInput";

import Prompt from "lib/components/prompt/Prompt";

import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import FeedbackIconButton from "utils/FeedbackIconButton";
import { asTitle } from "utils/Utils";
import { ChangeEvent } from "utils/history/Event";
import History from "utils/history/History";

import { updateTrajectoryForm } from "./TrajectoryService";
import ExpressionParser from "core/field/parser/ExpressionParser";

type Information = {
	fields: Field[];
};

type Form = {
	information: Information;
};

export type FormViewProps = {
	trajectoryId: number;
	fields: Field[];
	filter: string;
	rights: InformationRights;

	onSaveForm(fields: Field[]): void;
};

const FormView: React.FC<FormViewProps> = (props: FormViewProps) => {
	const { t } = useTranslation();

	const [data, setData] = useState<Form>({
		information: { fields: props.fields }
	});
	const [invalidFields, setInvalidFields] = useState<number[]>([]);
	const [history, setHistory] = useState(new History<Form>(data));

	const [edit, setEdit] = useState(false);

	const [successMessage, setSuccessMessage] = useState<string>();
	const [errorMessage, setErrorMessage] = useState<string>();

	const parser = useMemo(
		() => new ExpressionParser(props.fields),
		[props.fields]
	);

	useEffect(() => {
		const newData = { information: { fields: props.fields } };
		setData(newData);
		setInvalidFields([]);
		setHistory(new History<Form>(newData));
	}, [props.fields]);

	function matches(field: Field, filter: string): boolean {
		if (filter === "") {
			return true;
		}

		if (field.name.toLowerCase().includes(filter)) {
			return true;
		}

		// TODO:
		// if (field.value !== null) {
		// 	if (field.value.toLowerCase().includes(filter)) {
		// 		return true;
		// 	}
		// }

		return false;
	}

	const saveForm = useCallback((): Promise<void> => {
		const actions = history.getActions();
		if (actions.length === 0) {
			return Promise.resolve();
		}

		if (invalidFields.length > 0) {
			setErrorMessage(t("invalidDocumentContent"));
			return Promise.resolve();
		}

		const patches = actions.flatMap(action => action.patches());
		return updateTrajectoryForm(props.trajectoryId, patches)
			.then(() => {
				setEdit(false);
				setHistory(new History<Form>(data));
			})
			.then(() => props.onSaveForm(data.information.fields))
			.then(() => {
				setSuccessMessage(
					t("saveDocumentSuccess", {
						document: "form"
					})
				);
				setErrorMessage(undefined);
			})
			.catch((reason: Error) => {
				let errorMessage: string;
				if (reason.message === ERR_INTERNET_DISCONNECTED) {
					errorMessage = t("internetDisconnectedError");
				} else {
					errorMessage = t("saveDocumentFailure", {
						document: "form"
					});
				}

				setSuccessMessage(undefined);
				setErrorMessage(errorMessage);
			});
	}, [props.trajectoryId, props.onSaveForm, history, data]);

	const closeForm = useCallback((): void => {
		if (
			!history.hasPrevious() ||
			window.confirm(t("exitDocumentMessage"))
		) {
			setEdit(false);
			setData({ information: { fields: props.fields } });
			setHistory(
				new History<Form>({ information: { fields: props.fields } })
			);
		}
	}, [history]);

	function updateField(
		index: number,
		event: ChangeEvent,
		valid: boolean
	): void {
		event.applyContext("/information/fields/" + index);

		setData(history.apply(event.toAction()));
		setInvalidFields(previous =>
			valid ? previous.filter(id => id !== index) : previous.concat(index)
		);
	}

	return (
		<>
			<Box sx={{ displayPrint: "none" }}>
				{!props.rights[""]?.[InformationRight.READ] && (
					<Alert severity="warning">
						{t("noAccess", { section: "information" })}
					</Alert>
				)}

				{errorMessage && (
					<Alert
						id="error-message"
						severity="error"
						onClose={(): void => setErrorMessage(undefined)}>
						{errorMessage}
					</Alert>
				)}
				{successMessage && (
					<Alert
						id="success-message"
						severity="success"
						onClose={(): void => setSuccessMessage(undefined)}>
						{successMessage}
					</Alert>
				)}
			</Box>

			{props.rights[""]?.[InformationRight.READ] && !edit && (
				<Grid container sx={{ displayPrint: "none" }}>
					<Grid item>
						<Button
							variant="outlined"
							onClick={() => setEdit(true)}
							startIcon={<EditIcon />}>
							{asTitle(t("editForm"))}
						</Button>
					</Grid>
					<Grid item>
						<Tooltip title={asTitle(t("print"))}>
							<span>
								<IconButton
									aria-label="print"
									size="large"
									disabled={edit}
									onClick={window.print}>
									<PrintIcon />
								</IconButton>
							</span>
						</Tooltip>
					</Grid>
				</Grid>
			)}

			{props.rights[""]?.[InformationRight.READ] && edit && (
				<Grid container>
					<Grid item>
						<Tooltip title={asTitle(t("save"))}>
							<span>
								<FeedbackIconButton
									id="save-form-button"
									aria-label="save-form"
									disabled={!history.hasPrevious()}
									onClick={saveForm}>
									<SaveIcon />
								</FeedbackIconButton>
							</span>
						</Tooltip>
					</Grid>
					<Grid item>
						<Tooltip title={asTitle(t("cancel"))}>
							<span>
								<IconButton
									id="close-form-button"
									aria-label="close-form"
									onClick={closeForm}>
									<CloseIcon />
								</IconButton>
							</span>
						</Tooltip>
					</Grid>
				</Grid>
			)}

			{props.rights[""]?.[InformationRight.READ] && (
				<Grid id="form" container rowSpacing={2} columnSpacing={4}>
					{data.information.fields !== undefined &&
						data.information.fields
							.filter(field => {
								const lowerCaseFilter =
									props.filter.toLowerCase();
								return matches(field, lowerCaseFilter);
							})
							.filter(
								field =>
									props.rights[field.uuid]?.[
										InformationRight.READ
									] ?? false
							)
							.map((field, index) => {
								return (
									<FieldInput
										key={field.uuid}
										field={field}
										editable={edit}
										rights={props.rights}
										parser={parser}
										onChange={(event, valid) =>
											updateField(index, event, valid)
										}
									/>
								);
							})}
				</Grid>
			)}

			{edit && (
				<Prompt
					when={history.hasPrevious()}
					message={t("exitDocumentMessage")}
				/>
			)}
		</>
	);
};

export default FormView;
